import Button from "@Components/generic/button";
import TextField from "@Components/generic/mui-text-field";
import { Api } from "@Shared/schemas";
import Storage from "@Storage";
import { useApi } from "@hooks/index";
import useI18n from "@i18n";
import { Divider, MenuItem, Select, SelectChangeEvent, Stack, Typography, useTheme } from "@mui/material";
import { useEffect, useState } from "react";

const PortalOverride = () => {
  const { i18n } = useI18n();
  const emulatorHost = process.config.EMULATOR_HOST || "localhost";
  const presetUrls = [
    ...(emulatorHost ? [`http://${emulatorHost}:3000`] : []), // Auto inject local emulator host if set in build args
    "https://byb-portal-dev.web.app",
    "https://byb-portal-staging.web.app",
    "https://byb-vr-dev.web.app",
    "https://byb-vr-prod.web.app",
    "https://byb-mobile-dev.web.app",
    "https://byb-mobile-prod.web.app",
    "https://byb-dev-env.web.app",
    "https://byb-prod-v2-env.web.app",
    "https://be-your-best-web-portal.web.app",
    "https://byb-app-development-1.web.app/",
  ];

  const theme = useTheme();

  const [selection, setSelection] = useState<number>(0);
  const [urlField, setUrlField] = useState<string | null>(null);
  const [urls, setUrls] = useState<string[]>(presetUrls);
  const [cachedUrl, setCachedUrl] = useState<string[]>([]);

  const { call: getFirebaseChannels, data: firebaseChannels } = useApi<typeof Api.Firebase.GetChannels>({
    api: Api.Firebase.GetChannels,
    request: {
      site_id: import.meta.env.VITE_FIREBASE_PREVIEW_CHANNEL_SITE_ID,
    },
  });

  const handleChange = (e: SelectChangeEvent<number>) => {
    setSelection(parseInt(e.target.value as string));
  };

  const loadUrl = () => {
    Storage.set({ key: "devStoredUrl", data: urls[selection] });
    window.location.href = urls[selection];
  };

  const addUrl = () => {
    if (urlField) {
      // Hack to ensure http prefix;
      let fixUrl = urlField.indexOf("http") <= -1 ? `http://${urlField}` : urlField;
      setUrls([fixUrl, ...urls]);
      setCachedUrl([fixUrl, ...cachedUrl]);
    }
  };

  const getValidChannels = (firebaseChannels: any) => {
    return firebaseChannels.channels
      .filter((channel: any) => channel.expireTime) // Only include channels with expireTime
      .map((channel: any) => channel.url);
  };

  const refreshCache = () => {
    setUrlField("");
    const devStoredUrl = Storage.get({ key: "devStoredUrl" });
    if (devStoredUrl) {
      setCachedUrl(devStoredUrl);
      setUrls([devStoredUrl, ...presetUrls]);

      // Refresh Firebase channels after setting preset URLs
      if (firebaseChannels) {
        const validChannels = getValidChannels(firebaseChannels);
        setUrls((prev) => [...prev, ...validChannels]);
      }
    } else {
      setUrls(presetUrls);
    }
  };

  useEffect(() => {
    refreshCache();
    getFirebaseChannels();
  }, []);

  useEffect(() => {
    // Only add preview channels to list of urls
    if (firebaseChannels) {
      const validChannels = getValidChannels(firebaseChannels);

      setUrls([...urls, ...validChannels]);
    }
  }, [firebaseChannels]);

  return (
    <Stack
      spacing={1.5}
      sx={{
        padding: theme.spacing(2),
        fontSize: theme.spacing(1.75),
      }}
    >
      <Stack spacing={2}>
        <Typography gutterBottom variant="h6" color="dsBase.v200">
          {i18n("header.exit.admin.override")}
        </Typography>
        <Typography color="dsBase.v200" gutterBottom>{`${i18n("header.exit.admin.current-url")}: ${window.location.href}`}</Typography>
        <TextField
          fullWidth
          label={i18n("header.exit.admin.url")}
          size="small"
          variant="outlined"
          placeholder="192.168.1.1"
          value={urlField}
          onChange={(e) => {
            setUrlField(e.target.value);
          }}
          inputProps={{
            sx: {
              backgroundColor: "dsBase.v100",
              color: "dsBase.v200",
            },
          }}
        />
        <Button variant="contained" size="small" onClick={addUrl}>
          {i18n("header.exit.admin.add")}
        </Button>
        <Typography gutterBottom variant="h7" color="dsBase.v200">
          {i18n("header.exit.admin.cached-urls")}
        </Typography>
        <Select
          size="small"
          variant="outlined"
          fullWidth
          value={selection}
          onChange={(e: any) => {
            handleChange(e);
          }}
          inputProps={{
            sx: {
              backgroundColor: "dsBase.v100",
              color: "dsBase.v200",
            },
          }}
        >
          {urls.map((url, i) => (
            <MenuItem key={i} value={i}>
              <Typography variant="textBase500" color="dsBase.v200">
                {url}
              </Typography>
            </MenuItem>
          ))}
        </Select>
        <Button variant="contained" size="small" onClick={loadUrl}>
          {i18n("header.exit.admin.load")}
        </Button>
        <Button
          variant="contained"
          size="small"
          color="secondary"
          onClick={() => {
            Storage.remove({ key: "devStoredUrls" });
            refreshCache();
          }}
        >
          {i18n("header.exit.admin.clear-url-cache")}
        </Button>
      </Stack>
      <Divider sx={{ width: "100%" }} />
      <Stack spacing={1.5}>
        <Typography gutterBottom variant="h6" color="dsBase.v200">
          {i18n("header.exit.admin.other")}
        </Typography>
        <Button
          variant="contained"
          size="small"
          color="secondary"
          onClick={() => {
            Storage.reset();
          }}
        >
          {i18n("header.exit.admin.clear-app-cache")}
        </Button>
      </Stack>
    </Stack>
  );
};

export default PortalOverride;
